import { useCallback, useEffect, useRef, useState } from "react";
import {
  BlockStack,
  InlineStack,
  InlineGrid,
  Text,
  Badge,
  Button,
  TextField,
  Spinner,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { formatPrice } from "../../utility/funcs";
import { previewPayable } from "../../utility/pagos";
import { clabeBankName, isValidClabe } from "../../utility/clabe";

// Returning a deposit CHARGED through Clip by SPEI, from Depósitos
// (pagos-svc payable `card_deposit_return`).
//
// A Clip refund only goes out while the day's Clip sales cover it, which left
// reservation 1599's $4,000 owed for days. A SPEI is instant — but a card has
// no bank account behind it, so the customer gives us a CLABE. The bank then
// says who owns it, and pagos-svc only pays an account that belongs to whoever
// paid the deposit (or one someone with services.pay accepted in writing).
//
// Everything shown here comes from deposit_return_party() and the server's
// preview: the same answers the payout guard uses, so the screen cannot say
// "lista" for an account pagos-svc would refuse.

const money = (c) => formatPrice(c / 100);
const last4 = (c) => (c ? `···${String(c).slice(-4)}` : "");
const POLL_MS = 5000;

export async function loadCardReturnParty(reservationId) {
  const { data, error } = await supabase.rpc("deposit_return_party", { p_reservation: reservationId });
  if (error) throw error;
  return (Array.isArray(data) ? data[0] : data) || null;
}

// The latest SPEI return of a Clip deposit for this reservation, if any.
export async function loadCardReturnPayout(reservationId) {
  const { data } = await supabase
    .from("payouts")
    .select("id, status, amount_cents, clabe, error, resolved_at, created_at")
    .eq("payable_kind", "card_deposit_return")
    .eq("payable_id", String(reservationId))
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return data || null;
}

export const cardReturnOpen = (p) => !!p && ["queued", "submitted", "awaiting_funds"].includes(p.status);

// Where the money would go, in the shape ConfirmDepositReturnModal shows.
const destinationOf = (party, preview) =>
  party?.clabe
    ? {
        clabe: party.clabe,
        holder: preview?.holder_name || party.holder_reported || party.name,
        bank: clabeBankName(party.clabe),
      }
    : null;

// Inside the Devolver Depósito modal. Reports through onReady whether the
// server would pay right now (and where), so the modal's primary action and
// the confirmation step can follow it.
export function CardReturnAccount({ reservationId, onReady }) {
  const [party, setParty] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(false);
  const [clabe, setClabe] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const p = await loadCardReturnParty(reservationId);
      setParty(p);
      const checking = ["queued", "pending"].includes(p?.verification_status);
      let pv = null;
      if (p?.clabe && p?.verification_status === "succeeded") {
        pv = await previewPayable("card_deposit_return", String(reservationId));
      }
      setPreview(pv);
      onReady?.({ ready: !!pv?.payable, destination: destinationOf(p, pv) });
      clearTimeout(timer.current);
      if (checking) timer.current = setTimeout(refresh, POLL_MS);
    } catch (e) {
      setErr(e.message || "No se pudo consultar la cuenta.");
      onReady?.({ ready: false, destination: null });
    }
  }, [reservationId, onReady]);

  useEffect(() => {
    refresh();
    return () => clearTimeout(timer.current);
  }, [refresh]);

  const digits = clabe.replace(/\D/g, "");
  const clabeError = digits.length === 18 && !isValidClabe(digits) ? "La CLABE no es válida; revisa los dígitos" : null;
  // `value` is the CLABE typed here, or the one already on the client's record.
  const submit = async (value = digits) => {
    setBusy(true);
    setErr(null);
    const { error } = await supabase.rpc("request_deposit_return_verification", {
      p_reservation: reservationId,
      p_clabe: value,
    });
    setBusy(false);
    if (error) return setErr(error.message);
    setEditing(false);
    setClabe("");
    refresh();
  };

  if (!party) {
    return err ? <Text tone="critical" variant="bodySm">{err}</Text> : <Spinner size="small" />;
  }

  const status = party.verification_status;
  const checking = ["queued", "pending"].includes(status);
  const ready = !!preview?.payable;
  const blockedMsg = preview?.blocked?.message;
  const showForm = editing || !party.clabe;
  // A CLABE already saved on the client but never checked by the bank: one
  // click checks it, no retyping.
  const unchecked = !!party.clabe && !party.verification_id;

  return (
    <BlockStack gap="200">
      <Text variant="bodySm" tone="subdued">
        Se devuelve por transferencia a una cuenta de {party.name}. El banco confirma que es suya antes de enviar.
      </Text>

      {!showForm && (
        <InlineGrid columns="1fr auto" alignItems="center" gap="200">
          <Text variant="bodySm">
            {[(ready && preview?.holder_name) || party.holder_reported || party.name,
              clabeBankName(party.clabe),
              last4(party.clabe)].filter(Boolean).join(" · ")}
          </Text>
          {unchecked ? (
            <Badge>Sin verificar</Badge>
          ) : checking ? (
            <Badge tone="info">Verificando con el banco…</Badge>
          ) : ready ? (
            <Badge tone="success">Verificada</Badge>
          ) : (
            <Badge tone="critical">No se puede usar</Badge>
          )}
        </InlineGrid>
      )}
      {!showForm && checking && (
        <Text variant="bodySm" tone="subdued">Suele tardar uno o dos minutos; esta ventana se actualiza sola.</Text>
      )}
      {!showForm && !unchecked && !checking && !ready && (
        <Text variant="bodySm" tone="critical">
          {status === "failed" ? "El banco no reconoce esta CLABE." : blockedMsg || "El banco no confirmó la cuenta."}
        </Text>
      )}

      {showForm ? (
        <InlineStack gap="200" blockAlign="end" wrap={false}>
          <div style={{ flex: 1 }}>
            <TextField
              label="CLABE del cliente"
              value={clabe}
              onChange={setClabe}
              autoComplete="off"
              inputMode="numeric"
              maxLength={22}
              error={clabeError || err || undefined}
              helpText="18 dígitos. Debe estar a nombre de quien pagó el depósito."
            />
          </div>
          <Button onClick={() => submit()} loading={busy} disabled={digits.length !== 18 || !!clabeError}>Verificar cuenta</Button>
          {party.clabe && <Button variant="plain" onClick={() => { setEditing(false); setErr(null); }}>Cancelar</Button>}
        </InlineStack>
      ) : (
        <InlineStack gap="300" blockAlign="center">
          {unchecked && (
            <Button onClick={() => submit(party.clabe)} loading={busy}>Verificar cuenta</Button>
          )}
          <Button variant="plain" onClick={() => setEditing(true)}>Usar otra CLABE</Button>
        </InlineStack>
      )}
      {!showForm && err && <Text variant="bodySm" tone="critical">{err}</Text>}
    </BlockStack>
  );
}

// One line on the Depósito card while the transfer is on its way, or when it
// failed and needs a person. Nothing once it succeeded: the card's own
// "Devuelto" line and comprobante cover that.
export function CardReturnStatus({ payout }) {
  if (!payout || payout.status === "succeeded" || payout.status === "canceled") return null;
  const badge = {
    queued: <Badge tone="info">Enviando</Badge>,
    submitted: <Badge tone="info">Enviando</Badge>,
    awaiting_funds: <Badge tone="warning">Pendiente de fondeo</Badge>,
    failed: <Badge tone="critical">Falló</Badge>,
    returned: <Badge tone="critical">Rebotó</Badge>,
  }[payout.status];
  if (!badge) return null;
  return (
    <BlockStack gap="100">
      <InlineGrid columns="1fr auto" alignItems="center">
        <Text variant="bodySm" tone="subdued">Transferencia {money(Number(payout.amount_cents))}</Text>
        {badge}
      </InlineGrid>
      {["failed", "returned"].includes(payout.status) && payout.error && (
        <Text variant="bodySm" tone="critical">{payout.error}</Text>
      )}
    </BlockStack>
  );
}
